import React, { useState } from 'react';
import {
  Plus, Edit2, Trash2, User, DollarSign, Briefcase, Mail, Phone,
  ArrowRight, Tag, ShieldAlert, CheckCircle, Clock, FileText, ChevronRight, Calendar
} from 'lucide-react';
import { Lead, PipelineType, resolveStatusColor } from '../types';
import { STAGES_CONFIG, StageType } from '../lib/stages';

interface KanbanBoardProps {
  leads: Lead[];
  onUpdatePipeline: (id: string, pipeline: PipelineType) => void;
  onDeleteLead: (id: string) => void;
  onEditLead: (lead: Lead) => void;
  onAddNewLeadToBoard: (pipeline: PipelineType) => void;
  searchQuery: string;
  statusColors: Record<string, string>;
  activeStageTab: StageType;
}

const BOARDS_CONFIG: { type: PipelineType; color: string; label: string }[] = [
  { type: 'Opportunity', color: '#0086f0', label: 'Opportunity' },
  { type: 'Won', color: '#00c875', label: 'Won' },
  { type: 'Unqualified', color: '#64748b', label: 'Unqualified' },
  { type: 'Lose', color: '#e2445c', label: 'Lose' },
  { type: 'Website Leads', color: '#0086f0', label: 'Website Leads' },
  { type: 'New Leads', color: '#a3a3a3', label: 'New Leads' },
  { type: 'Attempted to Contact', color: '#a25ddc', label: 'Attempted' },
  { type: 'Cold Call Leads', color: '#ff5c93', label: 'Cold Calls' },
  { type: 'Quoted', color: '#579bfc', label: 'Quoted' },
  { type: 'In Process', color: '#fdab3d', label: 'In Process' },
  { type: 'Awaiting First Shipment', color: '#22c55e', label: 'Awaiting Ship' },
  { type: 'Brokers', color: '#6366f1', label: 'Brokers' },
  { type: 'Shipper', color: '#f43f5e', label: 'Growers' },
  { type: 'Revisit Later | ADD FU DATE', color: '#64748b', label: 'Follow Up' },
  { type: 'Complete', color: '#00c875', label: 'Complete' },
];

export default function KanbanBoard({
  leads,
  onUpdatePipeline,
  onDeleteLead,
  onEditLead,
  onAddNewLeadToBoard,
  searchQuery,
  statusColors,
  activeStageTab,
}: KanbanBoardProps) {
  // Track which column is currently being dragged over
  const [dragOverColumn, setDragOverColumn] = useState<PipelineType | null>(null);


  // Filter leads based on the global search query
  const getFilteredLeadsForColumn = (columnType: PipelineType) => {
    return leads.filter((lead) => {
      if (lead.pipeline !== columnType) return false;
      
      const query = searchQuery.toLowerCase();
      return (
        (lead.name || '').toLowerCase().includes(query) ||
        (lead.company || '').toLowerCase().includes(query) ||
        (lead.email || '').toLowerCase().includes(query) ||
        (lead.owner && (lead.owner || '').toLowerCase().includes(query)) ||
        (lead.contactPerson && (lead.contactPerson || '').toLowerCase().includes(query)) ||
        (lead.service && lead.service.toLowerCase().includes(query)) ||
        (lead.industry && lead.industry.toLowerCase().includes(query))
      );
    });
  };

  // Drag-and-drop handlers
  const handleDragStart = (e: React.DragEvent, leadId: string) => {
    e.dataTransfer.setData('text/plain', leadId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnType: PipelineType) => {
    e.preventDefault();
    setDragOverColumn(columnType);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, columnType: PipelineType) => {
    e.preventDefault();
    setDragOverColumn(null);
    const leadId = e.dataTransfer.getData('text/plain');
    if (leadId) {
      onUpdatePipeline(leadId, columnType);
    }
  };

  // Status color pill helper
  const getStatusColorClasses = (status: string) => {
    const s = status.toLowerCase();
    if (s.includes('won') || s.includes('complete') || s.includes('success') || (s.includes('awarded') && !s.includes('not awarded'))) {
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/30';
    }
    if (s.includes('lost') || s.includes('not awarded') || s.includes('decline')) {
      return 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200/50 dark:border-rose-800/30';
    }
    if (s.includes('onboarding') || s.includes('process') || s.includes('working') || s.includes('active')) {
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200/50 dark:border-amber-800/30';
    }
    if (s.includes('quote') || s.includes('price')) {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200/50 dark:border-blue-800/30';
    }
    if (s.includes('contact') || s.includes('attempt') || s.includes('transfer')) {
      return 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-400 border border-purple-200/50 dark:border-purple-800/30';
    }
    return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700/50';
  };

  const activeStageConfig = STAGES_CONFIG.find(s => s.id === activeStageTab);
  const activeStagePipelines = activeStageConfig ? activeStageConfig.pipelines : [];
  const visibleColumns = BOARDS_CONFIG.filter((b) => activeStagePipelines.includes(b.type));

  return (
    <div className="flex-1 overflow-x-auto overflow-y-hidden pb-4 select-none">
      <div className="flex gap-4 px-1 min-h-[calc(100vh-220px)] h-full">
        {visibleColumns.map((board) => {

          const columnLeads = getFilteredLeadsForColumn(board.type);
          const columnValue = columnLeads.reduce((sum, lead) => sum + lead.dealValue, 0);
          const isDraggingOver = dragOverColumn === board.type;

          return (
            <div
              key={board.type}
              onDragOver={(e) => handleDragOver(e, board.type)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, board.type)}
              className={`w-64 shrink-0 flex flex-col rounded-xl border transition-all duration-200 ${
                isDraggingOver
                  ? 'border-blue-500 bg-blue-50/20 dark:bg-blue-950/10 ring-2 ring-blue-500/20 scale-[1.01]'
                  : 'border-slate-200 bg-slate-50/40 dark:border-slate-800 dark:bg-slate-900/40'
              }`}

            >
              {/* Column Header */}
              <div className="p-3.5 border-b border-slate-200 dark:border-slate-800 flex flex-col gap-1.5 shrink-0 bg-white dark:bg-slate-950 rounded-t-xl relative overflow-hidden">
                {/* Horizontal color stripe */}
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ backgroundColor: board.color }}
                />

                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: board.color }}
                    />
                    <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm tracking-tight truncate max-w-[150px]">
                      {board.label}
                    </h3>
                  </div>

                  <button
                    onClick={() => onAddNewLeadToBoard(board.type)}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-blue-500 rounded transition cursor-pointer"
                    title={`Add new lead to ${board.label}`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center justify-between text-xs font-mono font-semibold text-slate-500 dark:text-slate-400 mt-1">
                  <span>{columnLeads.length} {columnLeads.length === 1 ? 'lead' : 'leads'}</span>
                  <span className="text-emerald-600 dark:text-emerald-400">
                    ${columnValue.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Cards Container */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[300px]">
                {columnLeads.length === 0 ? (
                  <div className="h-full min-h-[150px] flex flex-col items-center justify-center border border-dashed border-slate-200 dark:border-slate-800 rounded-lg p-4 text-center">
                    <Briefcase className="w-5 h-5 text-slate-300 dark:text-slate-700 mb-1" />
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 font-medium font-mono">
                      Drag leads here
                    </p>
                  </div>
                ) : (
                  columnLeads.map((lead) => (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead.id)}
                      className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800/80 rounded-lg p-2.5 shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition duration-150 cursor-grab active:cursor-grabbing group"
                    >
                      {/* Company Name & Actions */}
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-[10px] font-bold font-mono tracking-wider text-slate-400 dark:text-slate-500 uppercase truncate">
                          {lead.company}
                        </span>
                        
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => onEditLead(lead)}
                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-blue-500 rounded transition cursor-pointer"
                            title="Edit Lead"
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => onDeleteLead(lead.id)}
                            className="p-1 hover:bg-red-50 dark:hover:bg-red-950/50 text-slate-400 hover:text-red-500 rounded transition cursor-pointer"
                            title="Delete Lead"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      {/* Lead Name */}
                      <h4
                        onClick={() => onEditLead(lead)}
                        className="font-bold text-slate-800 dark:text-slate-100 text-sm hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition line-clamp-1 mb-1"
                      >
                        {lead.name}
                      </h4>

                      {/* Info lines */}
                      <div className="space-y-0.5 text-xs text-slate-500 dark:text-slate-400 mb-2">
                        {lead.owner && (
                          <div className="flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate">{lead.owner}</span>
                          </div>
                        )}
                        {(lead.service || lead.industry) && (
                          <div className="flex items-center gap-1.5">
                            <Tag className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate">
                              {lead.service || '—'} • {lead.industry || '—'}
                            </span>
                          </div>
                        )}
                        {lead.followUpDate && (
                          <div className="flex items-center gap-1.5 pt-0.5">
                            <Calendar className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
                            <span className="font-bold text-[10px] text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-1.5 py-0.5 rounded border border-blue-100 dark:border-blue-900/40">
                              FU: {lead.followUpDate}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Bottom row: Deal Value & Status */}
                      <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800/80 pt-2 mt-1.5">
                        <div className="font-mono font-bold text-slate-700 dark:text-slate-200 text-xs inline-flex items-center gap-0.5">
                          <span className="text-slate-400 dark:text-slate-500 text-[10px] font-normal">$</span>
                          {lead.dealValue.toLocaleString()}
                        </div>

                        <span
                          className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-white border border-transparent"
                          style={{ backgroundColor: resolveStatusColor(lead.status, statusColors) }}
                        >
                          {lead.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}
